import { useNavigate } from 'react-router-dom';
import { useUiStore } from '@/stores/uiStore';
import BevelButton from './BevelButton';
import CategorySection from './CategorySection';
import {
  TOOLS_ITEMS,
  INFO_ITEMS,
  HELP_ITEM,
  type ToolsItem,
} from './sidebarItems';
import styles from './RightNav.module.css';

/**
 * Right rail — Tools (always-expanded) + Info + Help.
 *
 * Mirrors the left Sidebar's bevel-button + projected-popover style.
 * Categories carry a distinct subtle shade (Tools = amber wash, Info =
 * neutral) and a small mono caps header. Each button reveals its
 * label + one-liner blurb in a glass popover that projects toward the
 * panel's left edge on hover/focus.
 */
export function RightNav() {
  const navigate = useNavigate();
  const toggleFeedback = useUiStore((s) => s.toggleFeedback);

  const handleClick = (item: ToolsItem) => {
    if (item.action === 'feedback') {
      toggleFeedback();
      return;
    }
    if (item.route) navigate(item.route);
  };

  const renderItem = (item: ToolsItem) => (
    <BevelButton
      key={item.id}
      icon={<span aria-hidden="true">{item.glyph}</span>}
      label={item.label}
      blurb={item.blurb}
      tone={item.tone}
      popoverSide="left"
      ariaLabel={`${item.label} — ${item.blurb}`}
      onClick={() => handleClick(item)}
    />
  );

  return (
    <nav className={styles.rnav} aria-label="Tools and shortcuts">
      <div className={styles.categories}>
        {/* Tools (always expanded) */}
        <CategorySection label="Tools" shade="tools">
          {TOOLS_ITEMS.map(renderItem)}
        </CategorySection>

        {/* Info — feedback / partners / upgrade */}
        <CategorySection label="Info" shade="info">
          {INFO_ITEMS.map(renderItem)}
        </CategorySection>
      </div>

      <div className={styles.helpAnchor}>{renderItem(HELP_ITEM)}</div>
    </nav>
  );
}
